import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { isEqual } from 'es-toolkit';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogCoverLotsTransformed } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { AuctionCardClickedTrigger } from '@/utils/amplitude/analytics/catalog/trackAuctionCardClicked.analytics';
import { getCatalogUrl } from '@/utils/urls';

type Props = {
    catalogId: number;
    className?: string;
    featured?: boolean;
    isSEOExcluded: boolean;
    lazyLoadImages?: boolean;
    title: string;
};

// these are NOT temporary - :(
const customImages = {
    146331: 'https://images.liveauctioneers.com/static/mail/images/featured/20190724_handbags.gif',
    146504: 'https://images.liveauctioneers.com/static/mail/images/featured/20190724_stair.gif',
};

export const CoverLotsComponent = ({
    catalogId,
    className = '',
    featured = false,
    isSEOExcluded = false,
    lazyLoadImages,
    title,
}: Props) => {
    const {
        primaryImage,
        primaryImageHighDpi,
        primaryItemTitle,
        secondaryImage,
        secondaryImageHighDpi,
        secondaryItemTitle,
        secondaryItemUrl,
        tertiaryImage,
        tertiaryImageHighDpi,
        tertiaryItemTitle,
        tertiaryItemUrl,
    } = useAppSelector((state) => getCatalogCoverLotsTransformed(state, catalogId), isEqual);

    const catalogUrl = getCatalogUrl(catalogId, title);

    const single = !secondaryItemUrl && !tertiaryItemUrl;
    const double = secondaryItemUrl && !tertiaryItemUrl;
    const linkRel = isSEOExcluded ? 'nofollow' : '';

    const secondaryImageContainerClasses =
        'relative block h-1/2 w-full overflow-hidden [&:first-child]:border-b [&:first-child]:border-border-secondary';
    const secondaryImageClasses = 'absolute bottom-0 left-0 right-0 top-0 m-auto block max-h-[101%] max-w-[101%]';

    return (
        <div
            className={cn(
                'relative w-full border border-border-secondary',
                {
                    // If not featured, all the images are square,
                    'pb-[66.33333333333%]': !featured,
                    // otherwise they are rectangular
                    'pb-[42.85714286%] [&.featured]:pb-[42.85714286%]': featured,
                },
                className
            )}
            data-track-trigger={AuctionCardClickedTrigger.ImageClick}
        >
            <div className="absolute bottom-0 left-0 right-0 top-0">
                <div
                    className={cn(
                        'relative float-left block h-full w-[calc((100%/3)*2)] overflow-hidden border-r border-border-secondary text-center',
                        {
                            'w-1/2': double,
                            'w-full': single,
                        }
                    )}
                >
                    <RouteLink
                        className="block h-full w-full"
                        rel={linkRel}
                        to={catalogUrl}
                    >
                        <img
                            alt={primaryItemTitle}
                            className="absolute bottom-0 left-0 right-0 top-0 m-auto block max-h-[101%] max-w-[101%]"
                            loading={lazyLoadImages ? 'lazy' : 'eager'}
                            src={customImages[catalogId] || primaryImage}
                            srcSet={customImages[catalogId] || `${primaryImageHighDpi} 2x`}
                        />
                    </RouteLink>
                </div>
                <div
                    className={cn('inline-block h-full w-[calc(100%/3)]', {
                        hidden: single,
                        'w-1/2 [&&a]:h-full [&&a]:border-b-0': double,
                    })}
                >
                    {secondaryItemUrl && (
                        <div className={secondaryImageContainerClasses}>
                            <RouteLink
                                className="block h-full w-full"
                                rel={linkRel}
                                to={catalogUrl}
                            >
                                <img
                                    alt={secondaryItemTitle}
                                    className={secondaryImageClasses}
                                    loading={lazyLoadImages ? 'lazy' : 'eager'}
                                    src={secondaryImage}
                                    srcSet={`${secondaryImageHighDpi} 2x`}
                                />
                            </RouteLink>
                        </div>
                    )}
                    {tertiaryItemUrl && (
                        <div className={secondaryImageContainerClasses}>
                            <RouteLink
                                className="block h-full w-full"
                                rel={linkRel}
                                to={catalogUrl}
                            >
                                <img
                                    alt={tertiaryItemTitle}
                                    className={secondaryImageClasses}
                                    loading={lazyLoadImages ? 'lazy' : 'eager'}
                                    src={tertiaryImage}
                                    srcSet={`${tertiaryImageHighDpi} 2x`}
                                />
                            </RouteLink>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
